import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService,
    DataService
} from '@vendure/admin-ui/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Collection } from '../generated';
import { safeJSONParse } from '../utils';
import {
  CollectionItemType,
  defaultFeaturedCollectionData,
  FeaturedCollectionData,
  ProductCardShape,
  ProductCardType
} from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';

type DisplayFieldKey =
  | 'subtitle'
  | 'viewers'
  | 'likes'
  | 'ranking'
  | 'sold'
  | 'price'
  | 'buybutton';

@Component({
  selector: 'vdr-featured-collection-input',
  template: `
    <vdr-card>
        <!-- Collection Selector -->
        <div class="clr-row mb-2">
          <vdr-form-field [label]="'Collection'" class="clr-col-12">
            <vdr-dynamic-form-input
              [control]="collectionControl"
              [def]="{
                type: 'relation',
                entity: 'Collection',
                ui: {
                  component: 'relation-collection-selector',
                }
              }"
            >
            </vdr-dynamic-form-input>
          </vdr-form-field>
        </div>

        <div class="clr-row mb-2">
          <!-- Display Type -->
          <vdr-form-field [label]="'Display Type'" class="clr-col-6">
            <select [formControl]="displayTypeControl">
              <option value="Product">Product</option>
              <option value="ProductVariant">Product Variant</option>
            </select>
          </vdr-form-field>
          <!-- Product Card Type -->
          <vdr-form-field [label]="'Product Card Type'" class="clr-col-6">
            <select [formControl]="productCardTypeControl">
              <option value="regular">Regular</option>
              <option value="regular-type2">Regular Type 2</option>
              <option value="horizontal-type">Horizontal Type</option>
              <option value="left-asset-small">Left Asset Small</option>
              <option value="right-asset-small">Right Asset Small</option>
              <option value="left-asset-large">Left Asset Large</option>
              <option value="right-asset-large">Right Asset Large</option>
              <option value="top-asset-small">Top Asset Small</option>
              <option value="top-asset-large">Top Asset Large</option>
              <option value="full-asset">Full Asset</option>
            </select>
          </vdr-form-field>
        </div>

        <div class="clr-row mb-2">
          <!-- Cards Per Row -->
          <vdr-form-field [label]="'Cards Per Row'" class="clr-col-6">
            <select [formControl]="cardsPerRowControl">
              <option *ngFor="let num of [1, 2, 3, 4]" [value]="num">{{ num }}</option>
            </select>
          </vdr-form-field>
          <vdr-form-field [label]="'Mobile Cards Per Row'" class="clr-col-6">
            <select [formControl]="mobileCardsPerRowControl">
              <option *ngFor="let num of [1, 2]" [value]="num">{{ num }}</option>
            </select>
          </vdr-form-field>
        </div>

        <div class="clr-row mb-2">
          <vdr-form-field *ngIf="['regular', 'regular-type2'].includes(productCardTypeControl.value)" [label]="'Card Shape'" class="clr-col-6">
            <select [formControl]="productCardShapeControl">
              <option value="rectangle">Rectangle</option>
              <option value="square">Square</option>
            </select>
          </vdr-form-field>
          <vdr-form-field [label]="'Media Display Rounded Corners'" class="clr-col-6">
            <select [formControl]="productCardRoundedCornersControl">
              <option value="none">None</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="full">Full</option>
            </select>
          </vdr-form-field>
        </div>

        <!-- Display Fields Checkboxes (FILTERED BY CARD TYPE) -->
        <div class="clr-row mb-2 display-fields-container">
          <div class="clr-col-12 display-fields-title">
            Select Display Fields:
          </div>

          <div class="clr-col-12 display-fields-row primary" *ngIf="primaryDisplayFieldOptions.length">
            <label
              class="display-field-option"
              *ngFor="let opt of primaryDisplayFieldOptions; trackBy: trackByField"
            >
              <input type="checkbox" [formControl]="opt.control" />
              <span>{{ opt.label }}</span>
            </label>
          </div>

          <div class="clr-col-12 display-fields-row secondary" *ngIf="secondaryDisplayFieldOptions.length">
            <label
              class="display-field-option"
              *ngFor="let opt of secondaryDisplayFieldOptions; trackBy: trackByField"
            >
              <input type="checkbox" [formControl]="opt.control" />
              <span>{{ opt.label }}</span>
            </label>
          </div>

          <div
            class="clr-col-12 form-input-hint"
            *ngIf="primaryDisplayFieldOptions.length === 0 && secondaryDisplayFieldOptions.length === 0"
            style="color:#666;"
          >
            No optional display fields for this card type.
          </div>
        </div>

        <div *ngIf="isBuyButtonSelected()">
        <div class="clr-row mb-2">
          <vdr-form-field [label]="'Button Label'" class="clr-col-12">
            <vdr-dynamic-form-input [control]="buttonTextControl" [def]="textValueDef"></vdr-dynamic-form-input>
          </vdr-form-field>
        </div>
        <div class="clr-row mb-2">
          <vdr-form-field [label]="'Button Color'" class="clr-col-6">
            <select [formControl]="buttonColorModeControl">
              <option value="default">Use Theme Default</option>
              <option value="custom">Custom</option>
            </select>
          </vdr-form-field>
        </div>
        </div>

        <div class="clr-row mb-2" *ngIf="isBuyButtonSelected() && buttonColorModeControl.value === 'custom'">
          <vdr-form-field [label]="'Button Background Color'" class="clr-col-6">
            <input type="color" [formControl]="buttonBgColorControl" />
          </vdr-form-field>
          <vdr-form-field [label]="'Button Label Color'" class="clr-col-6">
            <input type="color" [formControl]="buttonTextColorControl" />
          </vdr-form-field>
        </div>
    </vdr-card>
  `,
  styleUrls: ['../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class FeaturedCollectionInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };
  private destroy$ = new Subject<void>();

  // Form controls
  collectionControl = new UntypedFormControl();
  displayTypeControl = new FormControl();
  productCardTypeControl = new FormControl();
  cardsPerRowControl = new FormControl();
  mobileCardsPerRowControl = new FormControl();
  productCardShapeControl = new FormControl();
  productCardRoundedCornersControl = new FormControl();
  buttonTextControl = new FormControl();
  buttonColorModeControl = new FormControl();
  buttonBgColorControl = new FormControl();
  buttonTextColorControl = new FormControl();

  // Display fields controls (kept stable; never reassign these)
  displayFieldsMap: Record<DisplayFieldKey, FormControl> = {
    subtitle: new FormControl(false),
    viewers: new FormControl(false),
    likes: new FormControl(false),
    ranking: new FormControl(false),
    sold: new FormControl(false),
    price: new FormControl(false),
    buybutton: new FormControl(false),
  };

  featuredCollectionData: FeaturedCollectionData;

  private allowedDisplayFields = new Set<DisplayFieldKey>();
  visibleDisplayFieldOptions: Array<{ key: DisplayFieldKey; label: string; control: FormControl }> = [];
  private readonly DISPLAY_FIELD_LABELS: Record<DisplayFieldKey, string> = {
    subtitle: 'Subtitle',
    viewers: 'Viewers',
    likes: 'Likes',
    ranking: 'Ranking',
    sold: 'Sold',
    price: 'Price',
    buybutton: 'Buy Button',
  };

  /**
   * Map card type -> fields that actually exist in your storefront components.
   * - regular / regular-type2 / horizontal-type: no metrics (viewers/likes/ranking/sold)
   * - v3 horizontal & v3 vertical variants: support metrics + sold
   */
  private readonly CARD_TYPE_ALLOWED_FIELDS: Record<string, DisplayFieldKey[]> = {
    'regular': ['subtitle', 'price', 'buybutton'],
    'regular-type2': ['subtitle', 'price', 'buybutton'],
    'horizontal-type': ['subtitle', 'price', 'buybutton'],

    'left-asset-small': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
    'left-asset-large': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
    'right-asset-small': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
    'right-asset-large': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],

    'top-asset-small': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
    'top-asset-large': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
    'full-asset': ['subtitle', 'viewers', 'likes', 'ranking', 'sold', 'price', 'buybutton'],
  };
  primaryDisplayFieldOptions: Array<{ key: DisplayFieldKey; label: string; control: FormControl }> = [];
  secondaryDisplayFieldOptions: Array<{ key: DisplayFieldKey; label: string; control: FormControl }> = [];
  private readonly PRIMARY_DISPLAY_FIELD_KEYS: DisplayFieldKey[] = ['subtitle', 'price', 'buybutton'];

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService
  ) {}

  ngOnInit() {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.featuredCollectionData = safeJSONParse<FeaturedCollectionData>(
        this.formControl.value,
        this.notificationService,
        defaultFeaturedCollectionData,
    )!;
    this.initializeFormControls();

    if (this.featuredCollectionData?.collectionId) {
      this.dataService.collection.getCollections({
        filter: {
          id: { eq: this.featuredCollectionData.collectionId.toString() }
        },
      }).mapSingle(data => data.collections?.items[0]).subscribe(collection => {
        this.collectionControl.setValue(collection);
      });
    }

    const selectedFields = new Set<DisplayFieldKey>((this.featuredCollectionData?.displayFields ?? []) as DisplayFieldKey[]);
    (Object.keys(this.displayFieldsMap) as DisplayFieldKey[]).forEach(k => {
      this.displayFieldsMap[k].setValue(selectedFields.has(k), { emitEvent: false });
    });
    this.refreshDisplayFieldUIAndPrune(this.productCardTypeControl.value as string, /*writeJson*/ false);
  }

  ngAfterViewInit() {
    // Basic field subscriptions
    this.collectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onCollectionChange);
    this.displayTypeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onDisplayTypeChange);
    this.cardsPerRowControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onCardsPerRowChange);
    this.mobileCardsPerRowControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onMobileCardsPerRowChange);
    this.productCardShapeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onProductCardShapeChange);
    this.productCardRoundedCornersControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onProductCardRoundedCornersChange);
    this.buttonTextControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onButtonTextChange);
    this.buttonColorModeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onButtonColorModeChange);
    this.buttonBgColorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onButtonBgColorChange);
    this.buttonTextColorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onButtonTextColorChange);

    // Card type subscription (refresh filtered options + prune)
    this.productCardTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: ProductCardType) => this.onProductCardTypeChange(val));

    // Subscribe to display field toggles
    (Object.entries(this.displayFieldsMap) as [DisplayFieldKey, FormControl][])
      .forEach(([fieldName, control]) => {
        control.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((checked: boolean) => this.onDisplayFieldChange(fieldName, checked));
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByField = (_: number, opt: { key: DisplayFieldKey }) => opt.key;

  private initializeFormControls() {
    this.displayTypeControl.setValue(this.featuredCollectionData?.displayType);
    this.productCardTypeControl.setValue(this.featuredCollectionData?.productCardType);
    this.cardsPerRowControl.setValue(this.featuredCollectionData?.cardsPerRow);
    this.mobileCardsPerRowControl.setValue(this.featuredCollectionData?.mobileCardsPerRow || 2);
    this.productCardShapeControl.setValue(this.featuredCollectionData?.productCardShape || ProductCardShape.Rectangle);
    this.productCardRoundedCornersControl.setValue(this.featuredCollectionData?.productCardRoundedCorners || 'none');
    this.buttonTextControl.setValue(this.toTextValueJson(this.featuredCollectionData?.buttonText || 'Shop Now'));
    this.buttonColorModeControl.setValue(this.featuredCollectionData?.buttonColorMode || 'default');
    this.buttonBgColorControl.setValue(this.featuredCollectionData?.buttonBgColor || null);
    this.buttonTextColorControl.setValue(this.featuredCollectionData?.buttonTextColor || null);
  }

  private onCollectionChange = (collection: Collection) => {
    const collectionId = collection ? (collection as any).id : null;
    if (this.featuredCollectionData?.collectionId !== collectionId) {
      this.featuredCollectionData!.collectionId = collectionId;
      this.updateJsonString();
    }
  }

  private onDisplayTypeChange = (value: CollectionItemType) => {
    if (this.featuredCollectionData?.displayType !== value) {
      this.featuredCollectionData!.displayType = value;
      this.updateJsonString();
    }
  }

  private onProductCardTypeChange = (value: ProductCardType) => {
    const next = value as unknown as string;
    const changed = this.featuredCollectionData?.productCardType !== value;
    if (changed) {
      this.featuredCollectionData!.productCardType = value;
    }
    this.refreshDisplayFieldUIAndPrune(next, true);
  }

  private onCardsPerRowChange = (value: number) => {
    if (this.featuredCollectionData?.cardsPerRow !== value) {
      this.featuredCollectionData!.cardsPerRow = value;
      this.updateJsonString();
    }
  }

  private onMobileCardsPerRowChange = (value: number) => {
    if (this.featuredCollectionData?.mobileCardsPerRow !== value) {
      this.featuredCollectionData!.mobileCardsPerRow = value;
      this.updateJsonString();
    }
  }

  private onProductCardShapeChange = (value: ProductCardShape) => {
    if (this.featuredCollectionData?.productCardShape !== value) {
      this.featuredCollectionData!.productCardShape = value;
      this.updateJsonString();
    }
  }

  private onProductCardRoundedCornersChange = (value: string) => {
    if (this.featuredCollectionData?.productCardRoundedCorners !== value) {
      this.featuredCollectionData!.productCardRoundedCorners = value;
      this.updateJsonString();
    }
  }

  private onButtonTextChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.featuredCollectionData?.buttonText) !== JSON.stringify(next)) {
      this.featuredCollectionData!.buttonText = next;
      this.updateJsonString();
    }
  }

  private onButtonColorModeChange = (value: string) => {
    if (this.featuredCollectionData?.buttonColorMode !== value) {
      this.featuredCollectionData!.buttonColorMode = value;
      this.updateJsonString();
    }
  }

  private onButtonBgColorChange = (value: string | null) => {
    if (this.featuredCollectionData?.buttonBgColor !== value) {
      this.featuredCollectionData!.buttonBgColor = value;
      this.updateJsonString();
    }
  }

  private onButtonTextColorChange = (value: string | null) => {
    if (this.featuredCollectionData?.buttonTextColor !== value) {
      this.featuredCollectionData!.buttonTextColor = value;
      this.updateJsonString();
    }
  }

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private onDisplayFieldChange(fieldName: DisplayFieldKey, checked: boolean) {
    if (!this.allowedDisplayFields.has(fieldName)) {
      this.displayFieldsMap[fieldName].setValue(false, { emitEvent: false });
      return;
    }

    const fields = new Set<DisplayFieldKey>((this.featuredCollectionData?.displayFields ?? []) as DisplayFieldKey[]);
    if (checked) fields.add(fieldName);
    else fields.delete(fieldName);

    this.featuredCollectionData!.displayFields = Array.from(fields);
    this.updateJsonString();
  }

  isBuyButtonSelected(): boolean {
    return this.allowedDisplayFields.has('buybutton') && this.displayFieldsMap.buybutton.value === true;
  }

  private refreshDisplayFieldUIAndPrune(cardType: string, writeJson: boolean) {
    const allowed = this.getAllowedDisplayFields(cardType);
    this.allowedDisplayFields = new Set(allowed);
    const allOptions = allowed.map(key => ({
      key,
      label: this.DISPLAY_FIELD_LABELS[key],
      control: this.displayFieldsMap[key],
    }));

    const primarySet = new Set<DisplayFieldKey>(this.PRIMARY_DISPLAY_FIELD_KEYS);
    this.primaryDisplayFieldOptions = allOptions.filter(o => primarySet.has(o.key));
    this.secondaryDisplayFieldOptions = allOptions.filter(o => !primarySet.has(o.key));

    const current = (this.featuredCollectionData?.displayFields ?? []) as DisplayFieldKey[];
    const pruned = current.filter(f => this.allowedDisplayFields.has(f));

    (Object.keys(this.displayFieldsMap) as DisplayFieldKey[]).forEach(k => {
      if (!this.allowedDisplayFields.has(k) && this.displayFieldsMap[k].value === true) {
        this.displayFieldsMap[k].setValue(false, { emitEvent: false });
      }
    });

    const changed = pruned.length !== current.length;
    if (changed) {
      this.featuredCollectionData!.displayFields = pruned;
    }
    if (writeJson) {
      this.updateJsonString();
    }
  }

  private getAllowedDisplayFields(cardType: string | null | undefined): DisplayFieldKey[] {
    const key = (cardType ?? '').toString();
    return this.CARD_TYPE_ALLOWED_FIELDS[key] ?? ([
      'subtitle',
      'viewers',
      'likes',
      'ranking',
      'sold',
      'price',
      'buybutton',
    ] as DisplayFieldKey[]);
  }

  private updateJsonString() {
    const jsonString = JSON.stringify(this.featuredCollectionData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
